import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLang, tf } from '../../utils/formation';
import './MembresFormation.css';

/**
 * Membres de la formation sur l'arbitrage commercial (CCMA - CACI).
 *
 * Affiche uniquement : nom, type, fonction, photo et un bouton pour télécharger le CV.
 * Données : GET {apiBaseUrl}/api/formation-membres (ERP Laravel). La liste (légère) est lue
 * une fois ; les cartes et leurs photos apparaissent ensuite par lots de 12 au défilement.
 * Tous les textes viennent de locales/{langue}/translation.json (bloc « formation »).
 * Filtres : type, recherche, fonction, « avec CV uniquement ». Tri : ordre par défaut,
 * nom A→Z / Z→A, fonction. Encadrants toujours avant participants.
 *
 * Props (toutes optionnelles) :
 *   lang        'fr' | 'ar' | 'en'   défaut : langue de la page (<html lang>, i18next), sinon 'fr'
 *   apiBaseUrl  URL de l'API         défaut : https://erp.caci.dz
 *   type        'encadrant' | 'participant'  n'afficher qu'un seul type (masque les onglets)
 *   promotion   ex. '2024-2025'      participants d'une promotion (les encadrants restent affichés)
 *   title, intro, showHeader         titre et introduction intégrés
 */

const DEFAULT_API_BASE_URL = 'https://erp.caci.dz';
const TYPES = ['encadrant', 'participant'];
const SORTS = ['default', 'name-asc', 'name-desc', 'role'];
const PAGE_SIZE = 12;

/** Textes de l'interface, lus dans translation.json (bloc « formation »). */
function buildTexts(translate, lang) {
  const text = (key, vars) => tf(translate, lang, `formation.${key}`, vars);

  return {
    title: text('title'),
    intro: text('intro'),
    all: text('all'),
    groups: { encadrant: text('groups.encadrant'), participant: text('groups.participant') },
    types: { encadrant: text('types.encadrant'), participant: text('types.participant') },
    filterLabel: text('filter_label'),
    searchLabel: text('search_label'),
    searchPlaceholder: text('search_placeholder'),
    advanced: text('advanced'),
    sortLabel: text('sort_label'),
    sorts: {
      default: text('sorts.default'),
      'name-asc': text('sorts.name_asc'),
      'name-desc': text('sorts.name_desc'),
      role: text('sorts.role'),
    },
    roleLabel: text('role_label'),
    allRoles: text('all_roles'),
    withCv: text('with_cv'),
    withCvChip: text('with_cv_chip'),
    quote: (query) => text('quote', { query }),
    reset: text('reset'),
    removeFilter: (label) => text('remove_filter', { label }),
    results: (n) => text(n === 1 ? 'results_one' : 'results_many', { n }),
    shown: (shown, total) => text('shown', { shown, total }),
    showMore: text('show_more'),
    allShown: text('all_shown'),
    downloadCv: text('download_cv'),
    cvShort: text('cv_short'),
    downloadCvOf: (name) => text('download_cv_of', { name }),
    loading: text('loading'),
    error: text('error'),
    retry: text('retry'),
    empty: text('empty'),
    noResult: text('no_result'),
  };
}

const HONORIFICS = new Set(['me', 'm.', 'mme', 'mlle', 'dr', 'pr', 'maître', 'maitre']);

function joinUrl(base, path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${String(base || '').replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;
}

function localized(member, field, lang) {
  if (lang !== 'fr') {
    const value = member[`${field}_${lang}`];
    if (value) return value;
  }
  return member[field] || '';
}

function initials(name) {
  const words = String(name)
    .split(/\s+/)
    .filter((word) => word && !HONORIFICS.has(word.toLowerCase()));
  if (words.length === 0) return '?';
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

function searchable(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function isUpperWord(word) {
  return word === word.toUpperCase() && word !== word.toLowerCase();
}

/** Clé de tri sur le nom de famille : « Me Ali HAROUN » -> « HAROUN Ali ». */
function surnameKey(member, lang) {
  if (lang === 'ar' && member.nom_ar) return member.nom_ar;
  if (member.nom_famille) return `${member.nom_famille} ${member.prenom || ''}`.trim();
  const words = String(member.nom || '')
    .split(/\s+/)
    .filter((word) => word && !HONORIFICS.has(word.toLowerCase()));
  let start = words.length;
  while (start > 0 && isUpperWord(words[start - 1])) start -= 1;
  if (start === 0 || start === words.length) return words.join(' ');
  return [...words.slice(start), ...words.slice(0, start)].join(' ');
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M12 4v11" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="fm-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="fm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function MemberCard({ member, lang, t, apiBaseUrl, photoFailed, onPhotoError, nameTag: NameTag = 'h4', delay = 0, showType = true }) {
  const name = localized(member, 'nom', lang);
  const role = localized(member, 'fonction', lang);
  const typeLabel = t.types[member.type] || member.type;
  const photo = photoFailed ? null : joinUrl(apiBaseUrl, member.photo || member.photo_url);
  const cv = joinUrl(apiBaseUrl, member.cv || member.cv_url);

  return (
    <li className={`fm-card fm-card--${member.type}`} style={{ animationDelay: `${delay}ms` }}>
      <div className="fm-portrait">
        {photo ? (
          <img src={photo} alt={name} width="400" height="500" loading="lazy" decoding="async"
            onError={() => onPhotoError(member.id)} />
        ) : (
          <span className="fm-monogram" aria-hidden="true">{initials(name)}</span>
        )}
        {showType ? <span className="fm-type" aria-hidden="true">{typeLabel}</span> : null}
      </div>
      <div className="fm-body">
        <NameTag className="fm-name">{name}</NameTag>
        {showType ? <p className="fm-visually-hidden">{typeLabel}</p> : null}
        {role && <p className="fm-role">{role}</p>}
        {cv && (
          <a className="fm-cv" href={cv} download aria-label={t.downloadCvOf(name)}>
            <DownloadIcon />
            <span className="fm-cv-long">{t.downloadCv}</span>
            <span className="fm-cv-short" aria-hidden="true">{t.cvShort}</span>
          </a>
        )}
      </div>
    </li>
  );
}

function Skeleton({ label }) {
  return (
    <div className="fm-skeleton" aria-busy="true">
      <p className="fm-visually-hidden" role="status">{label}</p>
      <ul className="fm-grid" aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
          <li key={i} className="fm-card">
            <div className="fm-portrait" />
            <div className="fm-skeleton-line" />
            <div className="fm-skeleton-line fm-skeleton-line--short" />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MembresFormation({
  lang: langProp,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  type,
  promotion,
  title,
  intro,
  showHeader = true,
}) {
  const { t: translate, i18n } = useTranslation();
  const lang = normalizeLang(langProp || i18n.language);
  const t = useMemo(() => buildTexts(translate, lang), [translate, lang]);
  const lockedType = TYPES.includes(type) ? type : null;
  const introText = intro !== undefined ? intro : t.intro;

  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [attempt, setAttempt] = useState(0);
  const [failedPhotos, setFailedPhotos] = useState({});

  const [filter, setFilter] = useState(lockedType || 'all');
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [withCv, setWithCv] = useState(false);
  const [sort, setSort] = useState('default');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setFilter(lockedType || 'all');
  }, [lockedType]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (lockedType) params.set('type', lockedType);
    if (promotion) params.set('promotion', promotion);
    const qs = params.toString();

    setStatus('loading');
    fetch(joinUrl(apiBaseUrl, `api/formation-membres${qs ? `?${qs}` : ''}`), {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setMembers(Array.isArray(data) ? data : []);
        setStatus('ready');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setStatus('error');
      });

    return () => controller.abort();
  }, [apiBaseUrl, lockedType, promotion, attempt]);

  const collator = useMemo(
    () => new Intl.Collator(lang, { sensitivity: 'base', numeric: true, ignorePunctuation: true }),
    [lang],
  );
  const trimmedQuery = query.trim();
  const needle = searchable(trimmedQuery);

  // `skip` ignore un critère : pour compter les onglets (type) et les fonctions (role)
  const matches = useCallback((m, skip) => {
    if (skip !== 'type' && filter !== 'all' && m.type !== filter) return false;
    if (skip !== 'role' && role && m.fonction !== role) return false;
    if (withCv && !m.cv) return false;
    if (needle && !searchable([m.nom, m.nom_ar, m.fonction, m.fonction_ar, m.fonction_en].join(' ')).includes(needle)) {
      return false;
    }
    return true;
  }, [filter, role, withCv, needle]);

  const counts = useMemo(() => {
    const base = members.filter((m) => matches(m, 'type'));
    return {
      all: base.length,
      encadrant: base.filter((m) => m.type === 'encadrant').length,
      participant: base.filter((m) => m.type === 'participant').length,
    };
  }, [members, matches]);

  const roleOptions = useMemo(() => {
    const byValue = new Map();
    members.forEach((m) => {
      if (!m.fonction) return;
      const option = byValue.get(m.fonction) || { value: m.fonction, label: localized(m, 'fonction', lang), count: 0 };
      if (matches(m, 'role')) option.count += 1;
      byValue.set(m.fonction, option);
    });
    return [...byValue.values()]
      .filter((option) => option.count > 0 || option.value === role)
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [members, matches, lang, collator, role]);

  const results = useMemo(() => {
    const byName = (a, b) => collator.compare(surnameKey(a.m, lang), surnameKey(b.m, lang));
    const byRole = (a, b) => {
      const ra = localized(a.m, 'fonction', lang);
      const rb = localized(b.m, 'fonction', lang);
      if (!ra !== !rb) return ra ? -1 : 1;
      return collator.compare(ra, rb) || byName(a, b);
    };
    return members
      .map((m, index) => ({ m, index }))
      .filter(({ m }) => matches(m))
      .sort((a, b) => {
        const byType = TYPES.indexOf(a.m.type) - TYPES.indexOf(b.m.type);
        if (byType) return byType;
        if (sort === 'name-asc') return byName(a, b) || a.index - b.index;
        if (sort === 'name-desc') return byName(b, a) || a.index - b.index;
        if (sort === 'role') return byRole(a, b) || a.index - b.index;
        return a.index - b.index; // ordre par défaut : celui défini dans l'ERP
      })
      .map(({ m }) => m);
  }, [members, matches, sort, collator, lang]);

  // Nouveaux critères : on repart des 12 premiers membres
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter, role, withCv, needle, sort, members]);

  const visible = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;
  const showMore = useCallback(() => setVisibleCount((count) => count + PAGE_SIZE), []);

  // Chargement au défilement : le lot suivant arrive quand la fin de la liste approche
  useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || !node || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) showMore();
    }, { rootMargin: '0px 0px 320px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, showMore]);

  const markPhotoFailed = useCallback((id) => {
    setFailedPhotos((previous) => ({ ...previous, [id]: true }));
  }, []);

  const resetFilters = () => {
    setRole('');
    setWithCv(false);
    setQuery('');
    if (!lockedType) setFilter('all');
  };

  const hasMembers = status === 'ready' && members.length > 0;
  const advancedCount = (role ? 1 : 0) + (withCv ? 1 : 0);
  const anyFilter = Boolean(role || withCv || trimmedQuery || (!lockedType && filter !== 'all'));
  const roleLabel = role ? (roleOptions.find((option) => option.value === role) || { label: role }).label : '';
  const chips = [
    role && { key: 'role', label: roleLabel, remove: () => setRole('') },
    withCv && { key: 'cv', label: t.withCvChip, remove: () => setWithCv(false) },
    trimmedQuery && { key: 'query', label: t.quote(trimmedQuery), remove: () => setQuery('') },
  ].filter(Boolean);

  const order = new Map(visible.map((m, index) => [m.id, index]));
  const groups = TYPES
    .map((key) => ({
      key,
      items: visible.filter((m) => m.type === key),
      total: results.filter((m) => m.type === key).length,
    }))
    .filter((group) => group.items.length > 0);

  // Sans en-tête intégré, la page fournit le h1 : sections en h2, noms en h3
  const SectionTag = showHeader ? 'h3' : 'h2';
  const nameTag = showHeader ? 'h4' : 'h3';

  return (
    <section className="fm-root" dir={lang === 'ar' ? 'rtl' : 'ltr'} lang={lang}
      aria-labelledby={showHeader ? 'fm-title' : undefined} aria-label={showHeader ? undefined : title || t.title}>
      {showHeader && (
        <header className="fm-header">
          <h2 id="fm-title" className="fm-title">{title || t.title}</h2>
          {introText && <p className="fm-intro">{introText}</p>}
        </header>
      )}

      {hasMembers && (
        <>
          <div className="fm-toolbar">
            {!lockedType && (
              <div className="fm-filters" role="group" aria-label={t.filterLabel}>
                {['all', ...TYPES].map((key) => (
                  <button key={key} type="button" className="fm-filter" aria-pressed={filter === key}
                    onClick={() => setFilter(key)}>
                    <span>{key === 'all' ? t.all : t.groups[key]}</span>
                    <span className="fm-filter-count">{counts[key]}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="fm-search">
              <label htmlFor="fm-search-input" className="fm-visually-hidden">{t.searchLabel}</label>
              <SearchIcon />
              <input id="fm-search-input" type="search" value={query} placeholder={t.searchPlaceholder}
                autoComplete="off" onChange={(event) => setQuery(event.target.value)} />
            </div>
          </div>

          <div className="fm-controls">
            <button type="button" className="fm-adv-toggle" aria-expanded={advancedOpen} aria-controls="fm-advanced"
              onClick={() => setAdvancedOpen((open) => !open)}>
              <FilterIcon />
              <span>{t.advanced}</span>
              {advancedCount > 0 && <span className="fm-badge">{advancedCount}</span>}
              <ChevronIcon />
            </button>
            <div className="fm-sort">
              <label htmlFor="fm-sort-select">{t.sortLabel}</label>
              <select id="fm-sort-select" className="fm-select" value={sort} onChange={(event) => setSort(event.target.value)}>
                {SORTS.map((key) => (
                  <option key={key} value={key}>{t.sorts[key]}</option>
                ))}
              </select>
            </div>
          </div>

          <div id="fm-advanced" className="fm-panel" hidden={!advancedOpen}>
            <div className="fm-field">
              <label htmlFor="fm-role-select" className="fm-field-label">{t.roleLabel}</label>
              <select id="fm-role-select" className="fm-select" value={role} onChange={(event) => setRole(event.target.value)}>
                <option value="">{t.allRoles}</option>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>{`${option.label} (${option.count})`}</option>
                ))}
              </select>
            </div>
            <label className="fm-check">
              <input type="checkbox" checked={withCv} onChange={(event) => setWithCv(event.target.checked)} />
              <span>{t.withCv}</span>
            </label>
            <button type="button" className="fm-link-button" onClick={resetFilters} disabled={!anyFilter}>
              {t.reset}
            </button>
          </div>

          <div className="fm-summary">
            <p className="fm-results" role="status">{t.results(results.length)}</p>
            {chips.length > 0 && (
              <ul className="fm-chips">
                {chips.map((chip) => (
                  <li key={chip.key}>
                    <button type="button" className="fm-chip" onClick={chip.remove} aria-label={t.removeFilter(chip.label)}>
                      <span>{chip.label}</span>
                      <CloseIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {status === 'loading' && <Skeleton label={t.loading} />}

      {status === 'error' && (
        <div className="fm-status" role="alert">
          <p>{t.error}</p>
          <button type="button" className="fm-button" onClick={() => setAttempt((n) => n + 1)}>{t.retry}</button>
        </div>
      )}

      {status === 'ready' && members.length === 0 && (
        <div className="fm-status"><p>{t.empty}</p></div>
      )}

      {hasMembers && results.length === 0 && (
        <div className="fm-status">
          <p>{t.noResult}</p>
          <button type="button" className="fm-button fm-button--ghost" onClick={resetFilters}>{t.reset}</button>
        </div>
      )}

      {hasMembers && groups.map((group) => (
        <section key={group.key} className="fm-section"
          aria-labelledby={lockedType ? undefined : `fm-section-${group.key}`}
          aria-label={lockedType ? t.groups[group.key] : undefined}>
          {/* Page dédiée à un seul type : le titre de la page suffit */}
          {lockedType ? null : (
            <SectionTag id={`fm-section-${group.key}`} className="fm-section-title">
              {t.groups[group.key]}
              <span className="fm-section-count">{group.total}</span>
            </SectionTag>
          )}
          <ul className="fm-grid">
            {group.items.map((member) => (
              <MemberCard key={member.id} member={member} lang={lang} t={t} apiBaseUrl={apiBaseUrl}
                photoFailed={Boolean(failedPhotos[member.id])} onPhotoError={markPhotoFailed} nameTag={nameTag} showType={!lockedType}
                delay={(order.get(member.id) % PAGE_SIZE) * 35} />
            ))}
          </ul>
        </section>
      ))}

      {hasMembers && results.length > 0 && (
        <div className="fm-more">
          {hasMore ? (
            <>
              <div ref={sentinelRef} className="fm-sentinel" aria-hidden="true" />
              <p className="fm-more-status">{t.shown(visible.length, results.length)}</p>
              <button type="button" className="fm-button fm-button--ghost" onClick={showMore}>{t.showMore}</button>
            </>
          ) : (
            results.length > PAGE_SIZE && <p className="fm-more-status">{t.allShown}</p>
          )}
        </div>
      )}
    </section>
  );
}
